import React, {useCallback, useState} from 'react';
import {isEqual} from "lodash-es";
import Validate, {ValidationResponse} from "@/helpers/validate";

export type GenericObject = { [key: string]: any }

type ValidateFun = typeof Validate[keyof typeof Validate];

type ValidateFunCurried = ReturnType<ValidateFun>;

const createFormFieldConfig = (
  key: string,
  value: any,
  valid: ValidationResponse,
  validateFun: Array<ValidateFun | ValidateFunCurried> | null,
  required: boolean,
  name?: string ) => {
  return {
    [key]: {
      name: name ?? key,
      value: value,
      valid: valid,
      validateFun: validateFun,
      required: required
    }
  };
};

export type FormReturnType = {
  setSubmitted: (value: (((prevState: boolean) => boolean) | boolean)) => void;
  handleSubmit: (event: React.FormEvent) => void;
  submitted: boolean;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  isInputFieldValid: (validateFun: Function[], value: any, name: string, required: boolean) => ({
    valid: boolean;
    message: any
  });
  form: GenericObject;
  handleChange: (event: (React.ChangeEvent | null), name: string, value: any) => void;
  isValid: () => boolean;
  resetForm: () => void;
  validateForm: () => void;
  makeFieldRequired: (name: string, required: boolean) => void;
  setForm: (value: GenericObject, initialValues?: boolean) => void;
  isFormChanged: () => (boolean);
  setInitialForm: (values: GenericObject) => void
}

const useForm = (callback?: () => void): FormReturnType => {
  const [initialFormValues, setInitialFormValues] = useState<GenericObject>({});
  const [form , setForm] = useState<GenericObject>({});
  const [submitted, setSubmitted] = useState(false);

  const isValid = useCallback(() => {
    return Object.values(form).every((value: any) => value.valid.valid);
  }, [form]);

  const isFormChanged = useCallback(() => {
    for (const [key, value] of Object.entries(form)) {
      if(!isEqual(value.value, initialFormValues[key].value)) {
        return true;
      }
    }

    return false;
  }, [form, initialFormValues]);

  const setInitialForm = useCallback((values: GenericObject) => {
    const initialFormValues: GenericObject = {};
    for (const [key, value] of Object.entries(values)) {
      initialFormValues[key] = { value: value.value };
      value.touched = false;
    }

    setForm(values);
    setInitialFormValues(initialFormValues);
    setSubmitted(false);
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  const isInputFieldValid = useCallback((validateFun: Function[], value: any, name: string, required: boolean) => {
    if(validateFun.length > 0) {
      for (const rule of validateFun) {
        const valid = rule(value, name, required);
        if (!valid.valid) {
          return { valid: false, message: valid.message };
        }
      }
    }

    return { valid: true, message: '' };
  }, []);

  const handleChange = useCallback((event: React.ChangeEvent | null, name: string, value: any) => {
    event?.persist(); // dropdowns dont send events

    setForm((prev) => {
      const field = prev[name];
      return {
        ...prev,
        [name]: {
          ...field,
          value,
          touched: true,
          valid: field.validateFun
            ? isInputFieldValid(field.validateFun, value, field.name ?? name, field.required)
            : { valid: true, message: '' },
        },
      };
    });
  }, [isInputFieldValid]);

  const handleSubmit = useCallback((event: React.FormEvent) => {
    event.preventDefault();
    callback?.();
  }, [callback]);

  const resetForm = () => {
    setForm({});
    setInitialFormValues({});
    setSubmitted(false);
  };

  const validateForm = useCallback(() => {
    setForm((form) => {
      const next: GenericObject = {};
      for (const [name, field] of Object.entries(form)) {
        next[name] = {
          ...field,
          ...(field.validateFun && {
            valid: isInputFieldValid(field.validateFun, field.value, field.name, field.required),
          }),
        };
      }
      return next;
    });
  }, [isInputFieldValid]);

  const makeFieldRequired = useCallback((name: string, required: boolean) => {
    setForm((form) => {
      const field = form[name];
      const validateFun = [...(field.validateFun ?? []), Validate.required]
        .filter((validate) => required ? true : validate !== Validate.required);
      return {
        ...form,
        [name]: {
          ...field,
          required,
          valid: isInputFieldValid(validateFun, field.value, field.name, required),
          validateFun,
        },
      };
    });
  }, [isInputFieldValid]);

  const setFormValues = (formValues: GenericObject, initialValues?:boolean) => {
    setForm(formValues);
    if(initialValues) {
      const initialFormValues: GenericObject = {};
      for (const [key, value] of Object.entries(formValues)) {
        initialFormValues[key] = { value: value.value };
      }
      setInitialFormValues(initialFormValues);
    }
  };

  return { resetForm, isValid, submitted, setSubmitted, isFormChanged, form, setInitialForm, handleChange, handleSubmit,
    isInputFieldValid, setForm: setFormValues, validateForm, makeFieldRequired };
};

export {createFormFieldConfig, useForm};
